import { useEffect } from 'react'
import { ChainId } from '@nominex/nomiswap-sdk'
import { useSelector } from 'react-redux'
import { useWagmi } from 'libs/wagmi'
import { useFastRefreshEffect, useSlowRefreshEffect } from 'hooks/useRefreshEffect'
import { useAppDispatch } from 'state'
import { DeserializedPool, State } from 'state/types'
import { addressesEqual } from 'utils/addressHelpers'
import { fetchInitialPoolsData, fetchPoolsPublicDataAsync, fetchPoolsUserDataAsync } from '.'
import { transformPool } from './helpers'

export const useInitialPoolsData = () => {
  const dispatch = useAppDispatch()
  const { chainId } = useWagmi()

  useEffect(() => {
    if (chainId) {
      dispatch(fetchInitialPoolsData({ chainId }))
    }
  }, [chainId, dispatch])
}

export const useFetchPublicPoolsData = (chainId: ChainId) => {
  const dispatch = useAppDispatch()

  useSlowRefreshEffect(() => {
    dispatch(fetchPoolsPublicDataAsync(chainId))
  }, [dispatch])
}

export const useFetchUserPools = (account: string, chainId: ChainId) => {
  const dispatch = useAppDispatch()

  useFastRefreshEffect(() => {
    if (account) {
      dispatch(fetchPoolsUserDataAsync(account, chainId))
    }
  }, [account, chainId, dispatch])
}

export const usePools = (): { pools: DeserializedPool[]; userDataLoaded: boolean } => {
  const { pools, userDataLoaded } = useSelector((state: State) => ({
    pools: state.pools.data,
    userDataLoaded: state.pools.userDataLoaded,
  }))
  return { pools: pools.map(transformPool), userDataLoaded }
}

export const usePool = (poolId: number): { pool: DeserializedPool | null; userDataLoaded: boolean } => {
  const { findedPool, userDataLoaded } = useSelector((state: State) => ({
    findedPool: state.pools.data.find((pool) => pool.sousId === poolId),
    userDataLoaded: state.pools.userDataLoaded,
  }))
  return { pool: findedPool ? transformPool(findedPool) : null, userDataLoaded }
}

export const usePoolFromAddress = (address: string): DeserializedPool => {
  const pool = useSelector((state: State) =>
    state.pools.data.find(({ contractAddress }) => addressesEqual(contractAddress, address)),
  )
  return transformPool(pool)
}

export const useLivePools = (): { pools: DeserializedPool[]; userDataLoaded: boolean } => {
  const { pools, userDataLoaded } = usePools()
  return { pools: pools.filter(({ isPrivate, isFinished }) => !isPrivate && !isFinished), userDataLoaded }
}
